import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { api } from "./api";

interface AuthState {
  authenticated: boolean;
  loading: boolean;
  refresh: () => void;
  // Seed the auth cache synchronously after a successful login, so the next
  // render (and RequireAuth) sees authenticated=true without waiting for a
  // /me refetch — avoids a race where navigation outruns the invalidation.
  setAuthenticated: (id: number) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: api.whoami,
  });
  const value: AuthState = {
    authenticated: !!data?.authenticated,
    loading: isLoading,
    refresh: () => qc.invalidateQueries({ queryKey: ["auth", "me"] }),
    setAuthenticated: (id: number) =>
      qc.setQueryData(["auth", "me"], { authenticated: true, id }),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { authenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!authenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
